import { DisplayConfig, JsxgraphBehaviorConfig, LabelConfig, WidgetConfig } from '@examdojo/core/widget';
import type { ThemeColorName } from '@examdojo/styles';

// ------------------------------------------------------------------
// 1. PARAMS CONFIGURATION
// ------------------------------------------------------------------

export interface ArithmeticSequenceParams {
  /** First term (a or u₁) */
  a?: number;
  /** Common difference */
  d?: number;
  /** Number of terms to display */
  numTerms?: number;
}

// ------------------------------------------------------------------
// 2. DISPLAY CONFIGURATION
// ------------------------------------------------------------------

/**
 * Configuration for sequence features (bars, points, labels, etc.)
 */
export interface ArithmeticFeatureConfig {
  enabled?: boolean;
  label?: LabelConfig;
  color?: ThemeColorName;
}

/** Style options for the bars */
export interface BarStyle {
  width?: number;
  opacity?: number;
}

export interface ArithmeticDisplayConfig extends DisplayConfig {
  /** Show bars for each term */
  bars?: boolean | (ArithmeticFeatureConfig & { style?: BarStyle });
  /** Show points at the top of each bar */
  points?: boolean | ArithmeticFeatureConfig;
  /** Show connecting line between points */
  connectingLine?: boolean | ArithmeticFeatureConfig;
  /** Show term labels (u₁, u₂, etc.) */
  termLabels?: boolean | ArithmeticFeatureConfig;
  /** Show value labels on bars */
  valueLabels?: boolean | ArithmeticFeatureConfig;
  /** Show the common difference arcs */
  differenceArcs?: boolean | ArithmeticFeatureConfig;
}

// ------------------------------------------------------------------
// 3. BEHAVIOR CONFIGURATION
// ------------------------------------------------------------------

/**
 * Arithmetic sequence-specific action targets.
 */
export type ArithmeticActionTarget = 'canvas' | 'bars' | 'points' | 'connectingLine';

export type ArithmeticActionType = 'UPDATE_PARAMS' | 'FIT_TO_SEQUENCE';

/**
 * Arithmetic sequence-specific behavior configuration.
 */
export type ArithmeticBehaviorConfig = JsxgraphBehaviorConfig<ArithmeticActionTarget, ArithmeticActionType>;

// ------------------------------------------------------------------
// 4. MASTER WIDGET CONFIG
// ------------------------------------------------------------------

export type ArithmeticSequenceWidgetConfig = WidgetConfig<
  'arithmetic-sequence',
  ArithmeticSequenceParams,
  ArithmeticDisplayConfig,
  ArithmeticBehaviorConfig
>;

// ------------------------------------------------------------------
// 5. DEFAULTS
// ------------------------------------------------------------------

export const DEFAULT_ARITHMETIC_PARAMS: Required<ArithmeticSequenceParams> = {
  a: 2,
  d: 3,
  numTerms: 6,
};

export const DEFAULT_ARITHMETIC_FEATURE_COLORS: Record<
  'bars' | 'points' | 'connectingLine' | 'differenceArcs',
  ThemeColorName
> = {
  bars: 'sky',
  points: 'sky',
  connectingLine: 'sky',
  differenceArcs: 'pink',
};
